import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Card,
    CardHeader,
    CardContent,
    Avatar,
    Typography,
} from '@material-ui/core';
import { toastr } from 'react-redux-toastr';
import TransferList from '@commonClient2/components/TransferList';
import * as dataSetManagerService from '../../../services/api-endpoints/dataSetManager.service';
import { extractErrorMessage } from '../../../../services/api';

const InputTitle = (props) => (
    <Typography
        component="p"
        className="input-label"
        {...props}
    >
        {props.children}
    </Typography>
);

/**
 * @param {{
 *  stepIndex: number
 *  validationErrors: {[Key: string] : boolean | string}
 *  errorRef: import('react').MutableRefObject<HTMLInputElement>
 *  formData: any
 *  updateFormData: Function
 * }} props
 */
function PeopleInformationSection({ stepIndex, validationErrors, errorRef, formData, updateFormData }) {
    const { t } = useTranslation('dataSetManager');
    const [availableFields, setAvailableFields] = useState([]);
    const [loading, setLoading] = useState(false);
    const loadingRef = useRef(false);

    const loadAvailableFields = useCallback(async () => {
        // Prevent multiple simultaneous calls
        if (loadingRef.current) {
            return;
        }

        loadingRef.current = true;
        setLoading(true);
        try {
            const response = await dataSetManagerService.getAvailableFields();
            // Store the full field objects with both key and label
            setAvailableFields(response.data);
        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            toastr.error(t('error.loadFieldsFailed'), errorMessage);
        } finally {
            setLoading(false);
            loadingRef.current = false;
        }
    }, [t]);

    // Load available fields from API on component mount
    useEffect(() => {
        loadAvailableFields();
    }, [loadAvailableFields]);

    const chosenFields = formData.fieldsSelected || [];

    // Convert fields to TransferList format
    // Use the field KEY as the ID (what gets sent to backend)
    // Use the field LABEL for display (what the user sees, including custom overrides)
    const transferListItems = availableFields.map((field) => ({
        id: field.key,
        label: field.label || field.key,
        selected: chosenFields.includes(field.key),
    }));

    const handleTransferListChange = (items) => {
        const selectedItems = items
            .filter((item) => item.selected)
            .map((item) => item.id);
        updateFormData('fieldsSelected', selectedItems);
    };

    return (
        <Card className="card">
            <CardHeader
                ref={errorRef}
                className="card--header"
                gutterbottom
                avatar={<Avatar className="avatar">{stepIndex}</Avatar>}
                title={t('sidePanel.peopleSection.title')}
            />
            <CardContent>
                <Typography component="p" className="input-label">
                    {t('sidePanel.peopleSection.subtitle')}
                </Typography>

                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                        <Typography>{t('loading')}</Typography>
                    </div>
                ) : (
                    <div style={{ width: '100%', overflow: 'hidden' }}>
                        <TransferList
                            items={transferListItems}
                            onChange={handleTransferListChange}
                            useSearchBars
                            useSelectAll
                            titleLeft={t('sidePanel.peopleSection.available')}
                            titleRight={t('sidePanel.peopleSection.selected')}
                            useTotalLeft
                            useTotalRight
                            width={240}
                        />
                    </div>
                )}

                {validationErrors.fieldsSelected && (
                    <Typography color="error" variant="caption">
                        {t('validation.fieldsRequired')}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
}

export default PeopleInformationSection;

